import React, { useEffect, useRef, useState } from "react";
import "./chatbot.css";
import { getAuthenticatedUser } from "@edx/frontend-platform/auth";
import { getConfig } from "@edx/frontend-platform";
import {
  getAuthenticatedHttpClient,
  getHttpClient,
} from "@edx/frontend-platform/auth";

import { marked } from "marked";
import { useParams } from "react-router";

const ChatBot = () => {
  const [messages, setMessages] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const messagesEndRef = useRef(null);

  const [input, setInput] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const user = getAuthenticatedUser();
  const sessionId = `${user.username}-${user.userId}`;
  const inputRef = useRef();
  const params = useParams();
  const courseId = params?.courseId;

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    // if (!courseId) return;

    setLoading(true);
    setError(null);
    setAnswer("");
    setMessages((prev) => [...prev, { sender: "user", text: trimmed }]);

    try {

      const chatUrl = new URL(
        `${getConfig().STUDIO_BASE_URL}/api/v1/chat/chatbot`
      );
      const response = await getAuthenticatedHttpClient().post(chatUrl.href, {
        chatbot_type: "studio",
        user_input: trimmed,
        ...(courseId && { course_id: courseId }),
      });

      if (response.status === 200) {
        const responseData = response.data.response;
        setAnswer(responseData);
        setMessages((prev) => [...prev, { sender: "bot", text: responseData }]);
      } else {
        throw new Error("Failed to get a valid response from the API.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setInput("");
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      // if (!courseId) return;

      try {
        const historyUrl = new URL(
          `${getConfig().STUDIO_BASE_URL}/api/v1/chat/chat-history`
        );
        const response = await getAuthenticatedHttpClient().post(
          historyUrl.href,
          {
            chatbot_type: "studio",
            ...(courseId && { course_id: courseId }),
          }
        );

        if (response.status === 200 && Array.isArray(response.data.response)) {
          const transformedMessages = response.data.response.map((item) => ({
            sender:
              item.type == "input"
                ? "user"
                : item.type == "output"
                  ? "bot"
                  : "",
            text: item.value || "",
          }));
          setMessages(transformedMessages);
        }
      } catch (error) {
        console.error("Error fetching chat history:", error);
      }
    };

    fetchHistory();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
    localStorage.setItem("authoringChatMessages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    if (isOpen) {
      inputRef.current?.focus();
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  return (
    <>
      <button
        className="chatbot-toggle"
        onClick={() => {
          setIsOpen(!isOpen);
          if (isMaximized) setIsMaximized(false);
        }}
      >
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 3C6.48 3 2 6.92 2 11.5c0 2.17 1.07 4.14 2.83 5.61L4 21l3.44-1.34c1.34.43 2.78.67 4.29.67 5.52 0 10-3.92 10-8.5S17.52 3 12 3zm-4 8a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm8 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm-8.31 4.9a5.995 5.995 0 0 0 8.62 0 1 1 0 0 0-1.5-1.32 4 4 0 0 1-5.62 0 1 1 0 1 0-1.5 1.32z" />
          </svg>
        )}
      </button>
      <div
        className={`chatbot-container ${isOpen ? "active" : ""} ${isMaximized ? "maximized" : "default"
          }`}
      >
        <div className="chatbot-header">
          <div className="chatbot-title">Ask anything...</div>

          <div className="chatbot-actions">
            <button
              className="chatbot-toggle-btn"
              onClick={() => setIsMaximized(!isMaximized)}
            >
              {!isMaximized ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14 3h7v7h-2V6.41l-5.3 5.3-1.4-1.41 5.29-5.29H14V3zM10 21H3v-7h2v4.59l5.3-5.3 1.4 1.41-5.29 5.29H10V21z" />
                </svg>
              ) : (
                <svg
                  width="16px"
                  height="16px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.1429 10H14M14 10V4.85714M14 10L20 4"
                    stroke="#000000"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.99996 14H9.99996M9.99996 14V19M9.99996 14L4 20"
                    stroke="#000000"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="chatbot-body">
          {messages.length === 0 && !loading && (
            <div className="chatbot-welcome">
              <p>Welcome to the chatbot! Ask me anything.</p>
            </div>
          )}
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`chat-message ${msg.sender === "user" ? "user" : "bot"
                }`}
              dangerouslySetInnerHTML={{ __html: marked.parse(msg.text) }}
            />
          ))}

          {loading && (
            <>
              <div className={`chat-message bot`}>
                <div className="loading-indicator">
                  <div class="chat-loading">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input-section">
          <input
            ref={inputRef}
            type="text"
            className="chatbot-input"
            placeholder="Message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            disabled={loading}
          />
          <button
            className={`chatbot-send-btn `}
            onClick={handleSend}
            disabled={loading || !input.trim()}
            title="Send message"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M12 19V5" />
              <path d="M5 12l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="chatbot-overlay" onClick={() => setIsOpen(false)} />
      )}
    </>
  );
};

export default ChatBot;
